import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { PresenceMonth, PresenceNow } from '../types/types';
import { israelToday, ymOf } from '../utils/schedule';

/* Presence in the Knesset building.
   - meta/presenceNow (~1 KB) is rewritten every minute by syncPresence. Every component
     that shows presence shares one copy: it's read when the first one mounts, then again
     every minute while a page that shows it is open and in view (at most twice a minute
     however many components ask).
   - presenceMonths/{YYYY-MM} is read once per page load per month; the current month is
     read again when the list of who's inside changes, since a stay is credited on exit. */

// Development builds only: ?presenceDemo=1 shows sample data (dev/presenceDemo), ?presenceDemo=0 turns it off
const demoOn = (): boolean => {
  try {
    const q = window.location.search;
    if (/[?&]presenceDemo=1/.test(q)) sessionStorage.setItem('presenceDemo', '1');
    if (/[?&]presenceDemo=0/.test(q)) sessionStorage.removeItem('presenceDemo');
    return sessionStorage.getItem('presenceDemo') === '1';
  } catch {
    return false;
  }
};

const REFRESH_MS = 60 * 1000;
// Under REFRESH_MS: the timer starts before the first read resolves, so an equal window
// would swallow every other refresh
const FRESH_MS = 30 * 1000;

let current: PresenceNow | null = null;
let fetchedAt = 0;
let inflight: Promise<PresenceNow | null> | null = null;
const listeners: Array<(p: PresenceNow | null) => void> = [];

const loadNow = (): Promise<PresenceNow | null> => {
  if (fetchedAt && Date.now() - fetchedAt < FRESH_MS) return Promise.resolve(current);
  if (!inflight) {
    const read: Promise<PresenceNow | null> = process.env.NODE_ENV === 'development' && demoOn()
      ? import('../dev/presenceDemo').then(m => m.demoPresenceNow())
      : getDoc(doc(db, 'meta', 'presenceNow')).then(snap => (snap.exists() ? (snap.data() as PresenceNow) : null));
    inflight = read.then(data => {
      current = data ? { ...data, present: data.present || {} } : null;
      fetchedAt = Date.now();
      inflight = null;
      listeners.slice().forEach(l => l(current));
      return current;
    }, (err: Error) => {
      inflight = null;
      throw err;
    });
  }
  return inflight;
};

// After a failed read (an error state's retry button): read again now
export const retryPresenceNow = (): void => {
  fetchedAt = 0;
  loadNow().catch(() => { /* the hooks keep showing the error */ });
};

export interface PresenceNowState {
  // undefined while loading, null when the doc doesn't exist
  now: PresenceNow | null | undefined;
  error: boolean;
  // Date.now(), updated every 30s, so durations and "updated N min ago" move on
  clock: number;
}

export const usePresenceNow = (): PresenceNowState => {
  const [now, setNow] = useState<PresenceNow | null | undefined>(fetchedAt ? current : undefined);
  const [error, setError] = useState(false);
  const [clock, setClock] = useState(() => Date.now());

  useEffect(() => {
    let alive = true;
    const onData = (p: PresenceNow | null) => {
      if (!alive) return;
      setNow(p);
      setError(false);
      setClock(Date.now());
    };
    listeners.push(onData);
    const refresh = () => { loadNow().then(onData).catch(() => { if (alive) setError(true); }); };
    refresh();
    const poll = window.setInterval(() => { if (document.visibilityState === 'visible') refresh(); }, REFRESH_MS);
    const tick = window.setInterval(() => { if (alive) setClock(Date.now()); }, 30 * 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      const i = listeners.indexOf(onData);
      if (i >= 0) listeners.splice(i, 1);
      window.clearInterval(poll);
      window.clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return { now, error, clock };
};

const monthPromises: Record<string, Promise<PresenceMonth | null>> = {};
const monthVersions: Record<string, string> = {};

// presenceMonths/{ym}; resolves null when nothing was credited that month. `version` (the
// presence list's changedAt) re-reads the current month after someone leaves.
export const getPresenceMonth = (ym: string, version = ''): Promise<PresenceMonth | null> => {
  const isCurrent = ym === ymOf(israelToday());
  const stale = isCurrent && monthVersions[ym] !== undefined && monthVersions[ym] !== version;
  if (!Object.prototype.hasOwnProperty.call(monthPromises, ym) || stale) {
    monthVersions[ym] = version;
    const read: Promise<PresenceMonth | null> = process.env.NODE_ENV === 'development' && demoOn()
      ? import('../dev/presenceDemo').then(m => m.demoPresenceMonth(ym))
      : getDoc(doc(db, 'presenceMonths', ym)).then(snap => (snap.exists() ? (snap.data() as PresenceMonth) : null));
    monthPromises[ym] = read
      .then(d => (d ? { ...d, month: d.month || ym, seconds: d.seconds || {}, days: d.days || {}, visits: d.visits || {} } : null))
      .catch((err: Error) => { delete monthPromises[ym]; delete monthVersions[ym]; throw err; });
  }
  return monthPromises[ym];
};

export interface PresenceMonthState {
  // undefined while loading
  month: PresenceMonth | null | undefined;
  error: boolean;
  retry: () => void;
}

export const usePresenceMonth = (ym: string, version: string): PresenceMonthState => {
  const [state, setState] = useState<{ ym: string; month: PresenceMonth | null | undefined; error: boolean }>({ ym, month: undefined, error: false });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    getPresenceMonth(ym, version)
      .then(m => { if (alive) setState({ ym, month: m, error: false }); })
      .catch(() => { if (alive) setState({ ym, month: undefined, error: true }); });
    return () => { alive = false; };
  }, [ym, version, attempt]);

  // A month that hasn't arrived yet shows as loading, not as the previous month
  const forThisMonth = state.ym === ym;
  return {
    month: forThisMonth ? state.month : undefined,
    error: forThisMonth && state.error,
    retry: () => setAttempt(a => a + 1),
  };
};
